import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  MenuItem,
} from '@mui/material';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function getInitialFormState(initialValues, defaultStatus) {
  if (!initialValues) {
    return {
      title: '',
      clientId: null,
      assigneeId: null,
      dueDate: '',
      priority: 'medium',
      status: defaultStatus,
      notes: '',
      description: '',
      isCompleted: false,
    };
  }

  return {
    title: initialValues.title || '',
    clientId: initialValues.client || null,
    assigneeId: initialValues.assignee || null,
    dueDate: initialValues.due_date || '',
    priority: initialValues.priority || 'medium',
    status: initialValues.status || defaultStatus,
    notes: initialValues.notes || '',
    description: initialValues.description || '',
    isCompleted: Boolean(initialValues.is_completed),
  };
}

function TaskDialog({
  open,
  onClose,
  onSubmit,
  statusOptions,
  clients,
  assignees,
  initialValues,
}) {
  const defaultStatus = statusOptions[0]?.value || 'backlog';
  const [formState, setFormState] = useState(() => getInitialFormState(initialValues, defaultStatus));

  useEffect(() => {
    setFormState(getInitialFormState(initialValues, defaultStatus));
  }, [initialValues, defaultStatus, open]);

  const clientValue = useMemo(
    () => clients.find((client) => client.id === formState.clientId) || null,
    [clients, formState.clientId],
  );
  const assigneeValue = useMemo(
    () => assignees.find((assignee) => assignee.id === formState.assigneeId) || null,
    [assignees, formState.assigneeId],
  );

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formState.title.trim() || !formState.clientId) {
      return;
    }

    onSubmit({
      title: formState.title.trim(),
      clientId: formState.clientId,
      assigneeId: formState.assigneeId,
      dueDate: formState.dueDate || null,
      priority: formState.priority,
      status: formState.status,
      notes: formState.notes,
      description: formState.description,
      isCompleted: formState.isCompleted,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{initialValues ? 'Edit task' : 'Create task'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {initialValues?.parent && (
              <Alert severity="info">
                Subtask of "
                {initialValues.parent_title || `Task #${initialValues.parent}`}
                ".
              </Alert>
            )}
            <TextField
              label="Title"
              value={formState.title}
              onChange={handleChange('title')}
              required
              fullWidth
            />
            <Autocomplete
              options={clients}
              value={clientValue}
              onChange={(_, value) => setFormState((prev) => ({ ...prev, clientId: value ? value.id : null }))}
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Client" required />}
            />
            <Autocomplete
              options={assignees}
              value={assigneeValue}
              onChange={(_, value) => setFormState((prev) => ({ ...prev, assigneeId: value ? value.id : null }))}
              getOptionLabel={(option) => option?.full_name || option?.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Assignee" placeholder="Optional" />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Due date"
                type="date"
                value={formState.dueDate || ''}
                onChange={handleChange('dueDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                select
                label="Priority"
                value={formState.priority}
                onChange={handleChange('priority')}
                fullWidth
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Status"
              value={formState.status}
              onChange={handleChange('status')}
              fullWidth
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              value={formState.notes}
              onChange={handleChange('notes')}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Description"
              value={formState.description}
              onChange={handleChange('description')}
              multiline
              minRows={3}
              fullWidth
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={formState.isCompleted}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      isCompleted: event.target.checked,
                      status:
                        event.target.checked && prev.status === 'completed'
                          ? prev.status
                          : event.target.checked
                          ? 'completed'
                          : prev.status === 'completed'
                          ? defaultStatus
                          : prev.status,
                    }))
                  }
                />
              )}
              label="Mark as completed"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {initialValues ? 'Save changes' : 'Create task'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

TaskDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  assignees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  initialValues: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    client: PropTypes.number,
    assignee: PropTypes.number,
    due_date: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.string,
    notes: PropTypes.string,
    description: PropTypes.string,
    is_completed: PropTypes.bool,
    parent: PropTypes.number,
    parent_title: PropTypes.string,
  }),
};

TaskDialog.defaultProps = {
  initialValues: null,
};

export default TaskDialog;
